<template>
  <div class="rich-text-editor">
    <div v-if="editor" class="rich-text-toolbar">
      <button type="button" class="btn btn-secondary btn-sm" :class="{ active: editor.isActive('bold') }" @click="editor.chain().focus().toggleBold().run()">
        Ж
      </button>
      <button type="button" class="btn btn-secondary btn-sm" :class="{ active: editor.isActive('italic') }" @click="editor.chain().focus().toggleItalic().run()">
        К
      </button>
      <button type="button" class="btn btn-secondary btn-sm" :class="{ active: editor.isActive('heading', { level: 2 }) }" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">
        H2
      </button>
      <button type="button" class="btn btn-secondary btn-sm" :class="{ active: editor.isActive('heading', { level: 3 }) }" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">
        H3
      </button>
      <button type="button" class="btn btn-secondary btn-sm" :class="{ active: editor.isActive('bulletList') }" @click="editor.chain().focus().toggleBulletList().run()">
        •
      </button>
      <button type="button" class="btn btn-secondary btn-sm" :class="{ active: editor.isActive('orderedList') }" @click="editor.chain().focus().toggleOrderedList().run()">
        1.
      </button>
      <template v-if="allowImages">
        <button type="button" class="btn btn-secondary btn-sm" :disabled="uploadingImage" @click="triggerImageUpload">
          {{ uploadingImage ? 'Загрузка...' : 'Фото в текст' }}
        </button>
        <input ref="imageInput" type="file" accept="image/jpeg,image/png,image/webp" hidden @change="onImageSelected" />
      </template>
    </div>
    <EditorContent :editor="editor" class="rich-text-content" />
    <p v-if="imageError" class="error-message">{{ imageError }}</p>
  </div>
</template>

<script setup>
import { onBeforeUnmount, watch } from 'vue'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { ref } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Расскажите о поездке...',
  },
  uploadImage: {
    type: Function,
    default: null,
  },
  allowImages: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const imageInput = ref(null)
const uploadingImage = ref(false)
const imageError = ref('')

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    StarterKit,
    Image.configure({ inline: false }),
    Placeholder.configure({ placeholder: props.placeholder }),
  ],
  onUpdate({ editor: currentEditor }) {
    emit('update:modelValue', currentEditor.getHTML())
  },
})

watch(
  () => props.modelValue,
  (value) => {
    if (editor.value && value !== editor.value.getHTML()) {
      editor.value.commands.setContent(value || '', false)
    }
  }
)

function triggerImageUpload() {
  imageError.value = ''
  imageInput.value?.click()
}

async function onImageSelected(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file || !editor.value || !props.uploadImage) return

  uploadingImage.value = true
  imageError.value = ''
  try {
    const { data } = await props.uploadImage(file)
    editor.value.chain().focus().setImage({ src: data.url }).run()
  } catch (err) {
    imageError.value = err.response?.data?.error || 'Не удалось загрузить фото'
  } finally {
    uploadingImage.value = false
  }
}

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style scoped>
.rich-text-editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rich-text-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.rich-text-toolbar .active {
  background: #dbeafe;
}

.rich-text-content :deep(.ProseMirror) {
  min-height: 180px;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  padding: 0.75rem;
  background: #fff;
}

.rich-text-content :deep(.ProseMirror:focus) {
  outline: 2px solid #93c5fd;
  outline-offset: 1px;
}

.rich-text-content :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin: 0.5rem 0;
}

.rich-text-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: #94a3b8;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}
</style>
